import React, { useEffect, useState } from "react";
import { Users, CreditCard, ShieldCheck, Loader2, UserPlus, Check, Mail, Plug, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function Settings() {
  const { user, refresh } = useAuth();
  const [workspace, setWorkspace] = useState(null);
  const [members, setMembers] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("viewer");
  const [busy, setBusy] = useState(false);
  const [digestBusy, setDigestBusy] = useState(false);
  const [digestEnabled, setDigestEnabled] = useState(true);

  const load = async () => {
    try {
      const [w, m] = await Promise.all([
        api.get("/workspaces/current"),
        api.get("/workspaces/current/members"),
      ]);
      setWorkspace(w.data);
      setMembers(m.data);
    } catch (err) {
      toast.error("Couldn't load workspace", { description: formatApiError(err) });
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (user && typeof user.email_weekly_digest === "boolean") {
      setDigestEnabled(user.email_weekly_digest);
    }
  }, [user]);

  const toggleDigest = async (next) => {
    setDigestEnabled(next); // optimistic
    setDigestBusy(true);
    try {
      await api.patch("/auth/me/preferences", { email_weekly_digest: next });
      if (refresh) await refresh();
      toast.success(next ? "Weekly digest enabled" : "Weekly digest disabled");
    } catch (err) {
      setDigestEnabled(!next); // revert
      toast.error("Couldn't update preference", { description: formatApiError(err) });
    } finally { setDigestBusy(false); }
  };

  const invite = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/workspaces/current/members", { email: inviteEmail, role: inviteRole });
      toast.success("Member added");
      setInviteEmail("");
      load();
    } catch (err) {
      toast.error("Couldn't add member", { description: formatApiError(err) });
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-8" data-testid="settings-page">
      <div>
        <p className="text-xs uppercase tracking-[0.22em] font-semibold text-blue-600">Settings</p>
        <h1 className="mt-1.5 font-display text-3xl font-semibold tracking-tight text-slate-900">Workspace settings</h1>
        <p className="mt-1.5 text-sm text-slate-600">Manage members, billing and security.</p>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="workspace-card">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-slate-500">
          <ShieldCheck className="h-3.5 w-3.5 text-blue-600" /> Workspace
        </div>
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field label="Name" value={workspace?.name} />
          <Field label="Plan" value={(workspace?.plan || "free").toUpperCase()} />
          <Field label="Your role" value={(workspace?.role || "viewer").toUpperCase()} />
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="notifications-card">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-slate-500">
          <Mail className="h-3.5 w-3.5 text-blue-600" /> Email notifications
        </div>
        <div className="mt-4 flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="font-medium text-slate-900 text-sm">Weekly compliance digest</div>
            <p className="mt-1 text-sm text-slate-600">
              Monday-morning rollup: total open issues by severity, top 5
              highest-risk items, and what changed in the last 7 days. Sent only
              to workspace owners.
            </p>
            {workspace?.role !== "owner" && (
              <p className="mt-2 text-xs text-slate-400">
                You're a {workspace?.role || "member"} in this workspace — the
                digest is sent to the owner.
              </p>
            )}
          </div>
          <div className="pt-1">
            <Switch
              checked={digestEnabled}
              onCheckedChange={toggleDigest}
              disabled={digestBusy}
              data-testid="weekly-digest-toggle"
              aria-label="Weekly digest email"
            />
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-slate-100 text-xs text-slate-500">
          High and critical scan findings always trigger an instant email to
          the runner and workspace owners — those alerts cannot be turned off.
        </div>
      </div>

      <ConnectedAccountsCard />

      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="members-card">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold text-slate-900 inline-flex items-center gap-2">
            <Users className="h-4 w-4 text-blue-600" /> Members
          </h2>
          <span className="text-xs text-slate-500">{members.length} total</span>
        </div>

        <ul className="mt-4 divide-y divide-slate-100">
          {members.map((m) => (
            <li key={m.user_id} className="py-3 flex items-center gap-3" data-testid={`member-${m.user_id}`}>
              <div className="h-8 w-8 rounded-full bg-blue-100 text-blue-700 inline-flex items-center justify-center font-semibold text-sm">
                {(m.name || m.email).slice(0, 1).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-900">{m.name || m.email}</div>
                <div className="text-xs text-slate-500">{m.email}</div>
              </div>
              <span className="rounded-full bg-slate-100 text-slate-700 text-[11px] font-semibold px-2.5 py-1 capitalize">{m.role}</span>
            </li>
          ))}
        </ul>

        {(workspace?.role === "owner" || workspace?.role === "admin") && (
          <form onSubmit={invite} className="mt-5 pt-5 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-[1fr_180px_auto] gap-2" data-testid="invite-form">
            <Input type="email" placeholder="teammate@company.com" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} className="h-10" data-testid="invite-email" required />
            <Select value={inviteRole} onValueChange={setInviteRole}>
              <SelectTrigger className="h-10" data-testid="invite-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="viewer">Viewer</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
            <Button type="submit" disabled={busy} className="rounded-lg bg-blue-600 hover:bg-blue-700 text-white h-10 px-5" data-testid="invite-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><UserPlus className="h-4 w-4 mr-2" />Add</>}
            </Button>
          </form>
        )}
        <p className="mt-3 text-xs text-slate-500">
          Invite-by-email is in preview — the user must register first, then you can add them by email. Full email invitations ship with the email integration.
        </p>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="billing-card">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="font-display text-lg font-semibold text-slate-900 inline-flex items-center gap-2">
              <CreditCard className="h-4 w-4 text-blue-600" /> Billing
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              You're on the <strong>{(workspace?.plan || "free").toUpperCase()}</strong> plan.
              Manage your subscription on the Billing page.
            </p>
          </div>
          <a
            href="/billing"
            className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 transition"
            data-testid="settings-manage-billing"
          >
            <CreditCard className="h-4 w-4" /> Manage billing
          </a>
        </div>
        <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4">
          <PlanCard
            name="Free" price="$0"
            features={["5 scans / mo", "Email digests", "1 workspace"]}
            current={(workspace?.plan || "free") === "free"}
          />
          <PlanCard
            name="Pro" price="$39"
            features={["100 scans / mo", "AI chat (300 msgs)", "PDF reports"]}
            current={workspace?.plan === "pro"}
            cta="/billing"
            ctaLabel="Upgrade to Pro"
          />
          <PlanCard
            name="Business" price="$149"
            features={["1,000 scans / mo", "AI chat (2,000 msgs)", "Priority support"]}
            current={workspace?.plan === "business"}
            cta="/billing"
            ctaLabel="Upgrade to Business"
          />
        </div>
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">{label}</div>
      <div className="mt-1 text-sm font-medium text-slate-900 truncate">{value || "—"}</div>
    </div>
  );
}

function PlanCard({ name, price, features, current, cta, ctaLabel }) {
  return (
    <div className={"rounded-xl border p-5 " + (current ? "border-blue-600 bg-blue-50/50" : "border-slate-200 bg-white")}>
      <div className="flex items-baseline justify-between">
        <div className="font-display text-lg font-semibold text-slate-900">{name}</div>
        {current && <span className="rounded-full bg-blue-600 text-white text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5">Current</span>}
      </div>
      <div className="mt-1 font-display text-3xl font-semibold text-slate-900">{price}<span className="text-sm text-slate-500 font-normal">/mo</span></div>
      <ul className="mt-3 space-y-1.5">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
            <Check className="h-3.5 w-3.5 mt-0.5 text-emerald-600 shrink-0" />{f}
          </li>
        ))}
      </ul>
      {current ? (
        <Button disabled className="mt-4 w-full rounded-lg bg-slate-200 text-slate-500 cursor-not-allowed">
          Current plan
        </Button>
      ) : cta ? (
        <a href={cta} className="mt-4 inline-flex items-center justify-center w-full rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold py-2 transition" data-testid={`settings-upgrade-${name.toLowerCase()}`}>
          {ctaLabel}
        </a>
      ) : (
        <Button disabled className="mt-4 w-full rounded-lg bg-slate-100 text-slate-400 cursor-not-allowed">
          —
        </Button>
      )}
    </div>
  );
}


/**
 * "Connected accounts" card — currently only Google Drive; designed to grow
 * (Slack, Notion, etc.) by adding more rows to the `integrations` list.
 *
 * Drive states:
 *  - loading     spinner
 *  - connected   shows connected email + Disconnect button
 *  - disconnected shows "Connect" button → kicks off OAuth (full-page nav)
 */
function ConnectedAccountsCard() {
  const [drive, setDrive] = useState(null);
  const [driveLoading, setDriveLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const loadDrive = async () => {
    try {
      const { data } = await api.get("/integrations/google/status");
      setDrive(data);
    } catch {
      setDrive({ connected: false });
    } finally { setDriveLoading(false); }
  };
  useEffect(() => { loadDrive(); }, []);

  const connectDrive = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/integrations/google/connect");
      window.location.assign(data.authorization_url);
    } catch (err) {
      toast.error("Couldn't start Google sign-in", { description: formatApiError(err) });
      setBusy(false);
    }
  };
  const disconnectDrive = async () => {
    setBusy(true);
    try {
      await api.post("/integrations/google/disconnect");
      toast.success("Google Drive disconnected");
      await loadDrive();
    } catch (err) {
      toast.error("Disconnect failed", { description: formatApiError(err) });
    } finally { setBusy(false); }
  };

  return (
    <div
      className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
      data-testid="connected-accounts-card"
    >
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-slate-500">
        <Plug className="h-3.5 w-3.5 text-blue-600" /> Connected accounts
      </div>
      <p className="mt-1 text-sm text-slate-600">
        Link external services to import documents and automate workflows.
      </p>

      <div className="mt-4 rounded-xl border border-slate-200 p-4 flex items-center gap-4 flex-wrap">
        <div className="shrink-0 h-10 w-10 rounded-lg bg-white ring-1 ring-slate-200 flex items-center justify-center">
          <SettingsGoogleDriveIcon className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-slate-900 text-sm">Google Drive</div>
          <div className="text-xs text-slate-500 mt-0.5">
            {driveLoading ? "Checking connection…"
              : drive?.connected ? (
                  <>
                    Connected as{" "}
                    <span className="text-slate-700 font-medium" data-testid="settings-drive-email">
                      {drive.email || "your Google account"}
                    </span>
                  </>
                )
              : "Not connected — import contracts directly from Drive."}
          </div>
        </div>
        <div>
          {driveLoading ? (
            <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
          ) : drive?.connected ? (
            <Button
              variant="outline" size="sm" onClick={disconnectDrive} disabled={busy}
              className="rounded-md text-slate-700 hover:text-rose-600 hover:border-rose-300"
              data-testid="settings-drive-disconnect"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><X className="h-3.5 w-3.5 mr-1.5" />Disconnect</>}
            </Button>
          ) : (
            <Button
              onClick={connectDrive} disabled={busy} size="sm"
              className="rounded-md bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="settings-drive-connect"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Connect"}
            </Button>
          )}
        </div>
      </div>

      <SlackIntegrationRow />
    </div>
  );
}

function SettingsGoogleDriveIcon({ className = "h-5 w-5" }) {
  // Duplicated tiny inline SVG so Settings doesn't need to import from
  // Documents.jsx (keeps the page self-contained).
  return (
    <svg viewBox="0 0 87.3 78" className={className} aria-hidden="true">
      <path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8H0c0 1.55.4 3.1 1.2 4.5z" fill="#0066da" />
      <path d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3L1.2 47.05a9.06 9.06 0 0 0-1.2 4.5h27.5z" fill="#00ac47" />
      <path d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502L65.5 76.8z" fill="#ea4335" />
      <path d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z" fill="#00832d" />
      <path d="m59.8 51.55h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" fill="#2684fc" />
      <path d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z" fill="#ffba00" />
    </svg>
  );
}


/**
 * Slack integration row inside the "Connected accounts" card.
 *
 * Three top-level states:
 *  1. Loading           — spinner
 *  2. plan_allows_slack=false  — small "Upgrade to Business" panel with link
 *  3. plan_allows_slack=true   — main flow:
 *       a. Not connected → "Connect Slack" button kicks off OAuth
 *       b. Connected → shows workspace + channel picker + frequency select +
 *          Send test + Disconnect
 *
 * Channels are loaded lazily when the user opens the picker — listing
 * channels costs Slack API calls and we don't want them on every page load.
 */
function SlackIntegrationRow() {
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [channels, setChannels] = useState(null);
  const [channelsLoading, setChannelsLoading] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get("/integrations/slack/status");
      setStatus(data);
    } catch {
      setStatus({ connected: false, plan_allows_slack: false });
    } finally { setStatusLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  // Surface ?slack_connected / ?slack_error from the OAuth round-trip exactly
  // once, then clean them off the URL so a refresh doesn't re-toast.
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("slack_connected") === "1") {
      toast.success("Slack connected", { description: "Pick a channel below." });
      sp.delete("slack_connected");
      window.history.replaceState({}, "", `${window.location.pathname}?${sp.toString()}`.replace(/\?$/, ""));
      refresh();
    }
    const err = sp.get("slack_error");
    if (err) {
      const map = {
        plan_required: "Slack integration requires the Business plan.",
        token_exchange: "Slack OAuth failed. Please try again.",
        invalid_state: "Slack OAuth session expired. Please try again.",
      };
      toast.error("Couldn't connect Slack", { description: map[err] || err });
      sp.delete("slack_error");
      window.history.replaceState({}, "", `${window.location.pathname}?${sp.toString()}`.replace(/\?$/, ""));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startOauth = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/integrations/slack/connect");
      window.location.assign(data.authorization_url);
    } catch (err) {
      toast.error("Couldn't start Slack sign-in", { description: formatApiError(err) });
      setBusy(false);
    }
  };

  const loadChannels = async () => {
    if (channels !== null) return;            // load once per session
    setChannelsLoading(true);
    try {
      const { data } = await api.get("/integrations/slack/channels");
      setChannels(data.channels || []);
    } catch (err) {
      toast.error("Couldn't load Slack channels", { description: formatApiError(err) });
    } finally { setChannelsLoading(false); }
  };

  const updateChannel = async (channelId) => {
    const ch = (channels || []).find((c) => c.id === channelId);
    setBusy(true);
    try {
      const { data } = await api.patch("/integrations/slack/settings", {
        channel_id: channelId, channel_name: ch?.name || null,
      });
      setStatus(data);
      toast.success("Channel saved");
    } catch (err) {
      toast.error("Couldn't save channel", { description: formatApiError(err) });
    } finally { setBusy(false); }
  };

  const updateFrequency = async (freq) => {
    setBusy(true);
    try {
      const { data } = await api.patch("/integrations/slack/settings", { frequency: freq });
      setStatus(data);
      toast.success("Notification frequency saved");
    } catch (err) {
      toast.error("Couldn't save frequency", { description: formatApiError(err) });
    } finally { setBusy(false); }
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      await api.post("/integrations/slack/test-message");
      toast.success("Test message sent", {
        description: `Check #${status.channel_name || "your channel"} on Slack.`,
      });
    } catch (err) {
      toast.error("Test message failed", { description: formatApiError(err) });
    } finally { setBusy(false); }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await api.post("/integrations/slack/disconnect");
      toast.success("Slack disconnected");
      setChannels(null);
      await refresh();
    } catch (err) {
      toast.error("Disconnect failed", { description: formatApiError(err) });
    } finally { setBusy(false); }
  };

  // ----- Render -----
  return (
    <div className="mt-3 rounded-xl border border-slate-200 p-4" data-testid="slack-integration-row">
      <div className="flex items-center gap-4 flex-wrap">
        <div className="shrink-0 h-10 w-10 rounded-lg bg-white ring-1 ring-slate-200 flex items-center justify-center">
          <SettingsSlackIcon className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-slate-900 text-sm flex items-center gap-2">
            Slack
            <span className="rounded-full bg-blue-50 text-blue-700 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5">
              Business
            </span>
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            {statusLoading ? "Checking connection…"
              : !status?.plan_allows_slack ? "Get realtime alerts and daily digests in Slack."
              : status?.connected ? (
                  <>
                    Connected to{" "}
                    <span className="text-slate-700 font-medium" data-testid="settings-slack-team">
                      {status.team_name || "your Slack workspace"}
                    </span>
                  </>
                )
              : "Get realtime alerts and daily digests in Slack."}
          </div>
        </div>
        <div>
          {statusLoading ? (
            <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
          ) : !status?.plan_allows_slack ? (
            <Button
              asChild size="sm"
              className="rounded-md bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="settings-slack-upgrade"
            >
              <a href="/billing">Upgrade</a>
            </Button>
          ) : status?.connected ? (
            <Button
              variant="outline" size="sm" onClick={disconnect} disabled={busy}
              className="rounded-md text-slate-700 hover:text-rose-600 hover:border-rose-300"
              data-testid="settings-slack-disconnect"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><X className="h-3.5 w-3.5 mr-1.5" />Disconnect</>}
            </Button>
          ) : (
            <Button
              onClick={startOauth} disabled={busy} size="sm"
              className="rounded-md bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="settings-slack-connect"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Connect Slack"}
            </Button>
          )}
        </div>
      </div>

      {/* ----- Configuration panel (only when connected on Business plan) ----- */}
      {status?.plan_allows_slack && status?.connected && (
        <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
              Channel
            </Label>
            <Select
              value={status.channel_id || undefined}
              onValueChange={updateChannel}
              onOpenChange={(o) => { if (o) loadChannels(); }}
            >
              <SelectTrigger className="mt-1.5 h-9 text-sm" data-testid="slack-channel-select">
                <SelectValue placeholder={channelsLoading ? "Loading channels…" : "Pick a channel"} />
              </SelectTrigger>
              <SelectContent>
                {channelsLoading && (
                  <div className="px-3 py-2 text-xs text-slate-500">Loading channels…</div>
                )}
                {!channelsLoading && (channels || []).length === 0 && channels !== null && (
                  <div className="px-3 py-2 text-xs text-slate-500">
                    No channels found. Add the AgentFlow bot to a channel in Slack first.
                  </div>
                )}
                {(channels || []).map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.is_private ? "🔒" : "#"} {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {status.channel_name && (
              <p className="mt-1 text-[11px] text-slate-500">
                Posting to <span className="font-medium">#{status.channel_name}</span>
              </p>
            )}
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
              When to notify
            </Label>
            <Select value={status.frequency || "realtime"} onValueChange={updateFrequency}>
              <SelectTrigger className="mt-1.5 h-9 text-sm" data-testid="slack-frequency-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="realtime">Realtime — Critical &amp; High only</SelectItem>
                <SelectItem value="daily">Daily digest — once a day at 9:00 UTC</SelectItem>
                <SelectItem value="both">Both — realtime + daily digest</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="sm:col-span-2 flex items-center justify-end gap-2">
            <Button
              variant="outline" size="sm" onClick={sendTest} disabled={busy || !status.channel_id}
              className="rounded-md"
              data-testid="slack-send-test"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Send test message"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function SettingsSlackIcon({ className = "h-5 w-5" }) {
  // Official Slack mark in brand colors (no PNG asset needed).
  return (
    <svg viewBox="0 0 270 270" className={className} aria-hidden="true">
      <path d="M99.4 151.2c0 7.1-5.8 12.9-12.9 12.9s-12.9-5.8-12.9-12.9 5.8-12.9 12.9-12.9h12.9v12.9zm6.5 0c0-7.1 5.8-12.9 12.9-12.9s12.9 5.8 12.9 12.9v32.3c0 7.1-5.8 12.9-12.9 12.9s-12.9-5.8-12.9-12.9v-32.3z" fill="#E01E5A" />
      <path d="M118.8 99.4c-7.1 0-12.9-5.8-12.9-12.9s5.8-12.9 12.9-12.9 12.9 5.8 12.9 12.9v12.9h-12.9zm0 6.5c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9H86.5c-7.1 0-12.9-5.8-12.9-12.9s5.8-12.9 12.9-12.9h32.3z" fill="#36C5F0" />
      <path d="M170.6 118.8c0-7.1 5.8-12.9 12.9-12.9s12.9 5.8 12.9 12.9-5.8 12.9-12.9 12.9h-12.9v-12.9zm-6.5 0c0 7.1-5.8 12.9-12.9 12.9s-12.9-5.8-12.9-12.9V86.5c0-7.1 5.8-12.9 12.9-12.9s12.9 5.8 12.9 12.9v32.3z" fill="#2EB67D" />
      <path d="M151.2 170.6c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9-12.9-5.8-12.9-12.9v-12.9h12.9zm0-6.5c-7.1 0-12.9-5.8-12.9-12.9s5.8-12.9 12.9-12.9h32.3c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9h-32.3z" fill="#ECB22E" />
    </svg>
  );
}
